import { computed, inject, onBeforeUnmount, provide, watch, watchEffect } from 'vue';
import { storeToRefs } from 'pinia';
import useUserStore from '@/store/user';
import { useConnectSocket } from '@/hooks/webSocket/connect';
import {
  GlobalSocketTypeEnum,
  IDENTITY_TYPE,
  SocketStatusEnum,
  SocketTypeEnum,
} from '@/enum/webSocket';
import mitt from 'mitt';
import { useRoute, useRouter } from 'vue-router';
import useChatListStore from '@/store/chatList';
import { ElMessage, ElNotification } from 'element-plus';
import dayjs from 'dayjs';
import { useDocumentVisibility, useWebNotification } from '@vueuse/core';
import pinia from '@/store';
import { isFunction } from 'lodash-es';
import { requestNotificationPermission, playNotificationSound } from '@/utils/notification';
import globleNotif from '@/components/globleNotif/index';
import Message from '@/models/message';
import { showBuyServiceTips } from '@/components/BuyServiceTips/state';
import { ChatType } from '@/enum/chat';

// 給其他組件做響使用的emitter
const diffusionEmitter = mitt();

export const useGlobalMsgListener = (type, fn) => {
  if (!isFunction(fn)) {
    throw new Error('useGlobalMsgListener 必須傳入一個fn');
  }
  diffusionEmitter.on(type, fn);

  onBeforeUnmount(() => {
    diffusionEmitter.off(type, fn);
  });
};

const useGlobalSocketListener = (router, route) => {
  const emitter = mitt();
  const { emit, on } = emitter;

  const chatListStore = useChatListStore(pinia);
  const userStore = useUserStore(pinia);
  const visibility = useDocumentVisibility();

  const {
    show: showNotification,
    close: closeNotification,
    onClick: onNotificationClick,
  } = useWebNotification({
    title: '先知銷售系統',
    lang: 'zh-HK',
  });

  // 點擊消息時打開標籤頁
  onNotificationClick((event) => {
    window.focus();

    // 如果能拿到數據，跳轉到對應的諮詢室
    const { data } = event.currentTarget;
    if (!data) return;

    const {
      msg_data: { room_id, from_user_type },
      teacher_id,
    } = data;

    if (!room_id || !teacher_id) return;

    router.push({
      path: `/home/chat/${teacher_id}/${from_user_type == 1 ? 'user' : 'teacher'}/room/${room_id}`,
    });
  });

  watchEffect(() => {
    console.log(`[${dayjs().format('YYYY-MM-DD HH:mm:ss')}]visibility: `, visibility.value);
  });

  on(GlobalSocketTypeEnum.MSG_NO_READ_NUM, (res) => {
    console.log(res);
    const { teacher_id, msg_data } = res;

    userStore.updateTeacherNotice(teacher_id, msg_data.no_read_msg_num);
  });

  const handleShowNotification = async (res, room_id) => {
    requestNotificationPermission();
    if (String(res.msg_data.room_id) !== room_id) {
      playNotificationSound();
    }

    if (visibility.value !== 'hidden') return;

    const { is_me, text, user, updated_at, is_newcomer } = res.msg_data;

    if (is_me) return;

    // 非新用户不需要發送消息通知
    if (!is_newcomer) return;

    // 頁面可見時發送系統右下角消息通知
    closeNotification(); // 先關閉上一條消息
    await showNotification({
      title: user.nickname,
      body: text,
      icon: user.avatar,
      data: res,
      timestamp: dayjs.unix(updated_at),
    });
  };

  on(GlobalSocketTypeEnum.SALE_NEW_MSG, (res) => {
    const { teacher_uid, room_id } = route.params;

    const { msg_data } = res;

    const { is_system } = msg_data;
    if (!is_system) {
      handleShowNotification(res, room_id);
    }

    if (![msg_data.from_user.uid, msg_data.to_user.uid].includes(teacher_uid)) {
      return;
    }

    chatListStore.updateChatItem(msg_data);
    diffusionEmitter.emit(GlobalSocketTypeEnum.SALE_NEW_MSG, msg_data);

    // 頁面可見時更新消息數
    function isWithinOneSecond(timestamp1, timestamp2) {
      return Math.abs(timestamp1 - timestamp2) <= 1;
    }

    if (visibility.value === 'visible' && isWithinOneSecond(res.msg_data.updated_at, res.time)) {
      const token = userStore.getTeacherTokenByUid(teacher_uid);
      chatListStore.getMsgNum({ token, route });
      return;
    }
  });

  on(GlobalSocketTypeEnum.SALE_CHAT_READ_MSG, (res) => {
    const { teacher_uid } = route.params;

    const { teacher_id, msg_data } = res;
    if (teacher_id !== teacher_uid) {
      return;
    }

    const user_id = msg_data.uid;
    chatListStore.readChatItem(teacher_id, user_id);
    diffusionEmitter.emit(GlobalSocketTypeEnum.SALE_CHAT_READ_MSG, {
      teacher_id,
      user_id,
    });
  });

  on(GlobalSocketTypeEnum.SALE_USER_NICKNAME_REMARK, (res) => {
    const { uid, nickname_remark } = res.msg_data;

    chatListStore.updateNicknameRemark(uid, nickname_remark);
    diffusionEmitter.emit(GlobalSocketTypeEnum.SALE_USER_NICKNAME_REMARK, {
      uid,
      nickname_remark,
    });
  });

  // 閃測訂單提醒
  on(GlobalSocketTypeEnum.SALE_SC_ORDER_NOTIFICATION, (res) => {
    const { msg } = res.msg_data;
    globleNotif(msg);
  });

  const goToBuyerChatroom = async ({ teacher_id, msg_data }) => {
    const directRoomId = msg_data.room_id;
    if (directRoomId) {
      await router.push({
        path: `/home/chat/${teacher_id}/user/room/${directRoomId}`,
      });
      return;
    }

    const token =
      userStore.getTeacherTokenByUid(teacher_id) ||
      userStore.userInfo?.teacher?.find((item) => String(item.uid) === String(teacher_id))?.token;
    if (!token) {
      ElMessage.warning('未能找到對應老師，請稍後重試。');
      return;
    }

    const { user_name } = msg_data;
    const userId = msg_data.user_id || msg_data.uid;
    if (userId) {
      const room = await Message.createRoom({ user_id: userId, token }).catch(() => null);
      if (room?.room_id) {
        await router.push({
          path: `/home/chat/${teacher_id}/user/room/${room.room_id}`,
        });
        return;
      }
    }

    // 目前協議沒有聊天室或用戶 ID 時，以老師與用戶名稱反查聊天室。
    const res = await Message.getFreeChatList({
      page: 1,
      size: 20,
      nickname: user_name,
      token,
      type: ChatType.ALL,
    }).catch(() => null);

    const room = res?.list?.find(
      (item) => item.nickname === user_name || item.nickname_remark === user_name,
    );
    if (!room?.room_id) {
      ElMessage.warning('未能找到與此用戶的聊天室，請稍後重試。');
      return;
    }

    await router.push({
      path: `/home/chat/${teacher_id}/user/room/${room.room_id}`,
    });
  };

  on(GlobalSocketTypeEnum.BUY_SERVICE_TIPS, (res) => {
    const { teacher_id, msg_data = {} } = res;
    const { service_name, user_name } = msg_data;
    if (!teacher_id || !service_name || !user_name) return;

    showBuyServiceTips({
      message: `用戶${user_name}：已成功下單您的「${service_name}」文字諮詢服務，請做好服務準備`,
      onClick: () => goToBuyerChatroom(res),
    });
  });

  return {
    emit,
  };
};

/**
 * 應用於全局範圍的一個web socket
 * 主要使用於未讀數更新、消息列表更新等情況下
 * 登錄後會自動連接
 */
export const useGlobalSocket = () => {
  const route = useRoute();
  const router = useRouter();
  const userStore = useUserStore();

  const { hasLogin, userInfo } = storeToRefs(userStore);
  const socket_uid = computed(() => userInfo.value?.socket_uid || '');

  watch(
    [hasLogin, socket_uid],
    ([hasLogin, socket_uid], _, onCleanup) => {
      if (!hasLogin || !socket_uid) {
        // 這裏只是一些提示。。。不重要
        !hasLogin && console.log('未登錄，global socket未連接');
        hasLogin && !socket_uid && console.log('沒有獲取到socket_uid：', socket_uid);
        return;
      }

      const { close, addResListener, open, send, status } = useConnectSocket(
        '全局',
        {
          from_uid: socket_uid,
          identity_type: IDENTITY_TYPE.SALE,
        },
        {
          debug: false,
          debugName: 'globalSocket',
          onReconnectFailed: () => {
            ElNotification.warning({
              message: '全局消息更新已斷開，請刷新（F5）重試',
              duration: 0,
            });
          },
        },
      );

      const stopStatusWatch = watch(status, (value) => {
        console.log(`${dayjs().format('YYYY-MM-DD HH:mm:ss')} globalSocketStatus: `, value);
        if (value === SocketStatusEnum.OPEN) {
          console.log('全局socket鏈接，更新notice');
          userStore.setDefaultNotReadCount();
        }
      });

      const { emit: emitListener } = useGlobalSocketListener(router, route);

      // 監聽 type = send 的socket
      addResListener(SocketTypeEnum.SEND, (res) => {
        const { msg_type } = res.data;

        emitListener(msg_type, {
          ...res.data,
          time: res.time,
        });
      });

      // 如果登錄狀態改變，關閉掉socket連接
      onCleanup(() => {
        stopStatusWatch();
        close();
        console.log('登錄狀態改變，global socket斷開連接');
      });
    },
    {
      immediate: true,
    },
  );
};
